import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  ChartBarIcon,
  FolderIcon,
  UserGroupIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { dashboardAPI } from '../services/api.ts';
import SalesGraph from '../components/SalesGraph.tsx';
import UsersGraph from '../components/UsersGraph.tsx';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getStatsData = () => {
    if (!stats) return [];

    if (user?.role === 'super_admin') {
      return [
        {
          name: 'Total Projects',
          stat: stats.totalProjects,
          icon: FolderIcon,
          gradient: 'from-blue-500 to-blue-600',
          bgGradient: 'from-blue-50 to-blue-100',
          iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
        },
        {
          name: 'Active Projects',
          stat: stats.activeProjects,
          icon: ChartBarIcon,
          gradient: 'from-primary-500 to-primary-600',
          bgGradient: 'from-primary-50 to-primary-100',
          iconBg: 'bg-gradient-to-br from-primary-500 to-primary-600',
        },
        {
          name: 'Total Users',
          stat: stats.totalEmployees,
          icon: UserGroupIcon,
          gradient: 'from-purple-500 to-purple-600',
          bgGradient: 'from-purple-50 to-purple-100',
          iconBg: 'bg-gradient-to-br from-purple-500 to-purple-600',
        },
        {
          name: 'Active Users',
          stat: stats.activeEmployees,
          icon: CalendarIcon,
          gradient: 'from-amber-500 to-amber-600',
          bgGradient: 'from-amber-50 to-amber-100',
          iconBg: 'bg-gradient-to-br from-amber-500 to-amber-600',
        },
      ];
    }

    return [
      {
        name: 'Total Employees',
        stat: stats.totalEmployees,
        icon: UserGroupIcon,
        gradient: 'from-purple-500 to-purple-600',
        bgGradient: 'from-purple-50 to-purple-100',
        iconBg: 'bg-gradient-to-br from-purple-500 to-purple-600',
      },
      {
        name: 'Active Employees',
        stat: stats.activeEmployees,
        icon: CalendarIcon,
        gradient: 'from-primary-500 to-primary-600',
        bgGradient: 'from-primary-50 to-primary-100',
        iconBg: 'bg-gradient-to-br from-primary-500 to-primary-600',
      },
      {
        name: 'Departments',
        stat: stats.departments,
        icon: BuildingOfficeIcon,
        gradient: 'from-blue-500 to-blue-600',
        bgGradient: 'from-blue-50 to-blue-100',
        iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      },
    ];
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600 absolute top-0 left-0"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-600 min-h-screen rounded-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <SparklesIcon className="h-8 w-8 text-primary-600" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {user?.role === 'super_admin'
                  ? 'System Overview'
                  : 'Project Overview'}
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 ml-11">
              Welcome back! Here's what's happening today.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="mb-10">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {getStatsData().map((item, index) => (
                <div
                  key={item.name}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-700 hover:-translate-y-1"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative p-6">
                    {/* Icon */}
                    <div
                      className={`inline-flex p-3 rounded-xl ${item.iconBg} shadow-lg mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <item.icon
                        className="h-7 w-7 text-white"
                        aria-hidden="true"
                      />
                    </div>

                    {/* Content */}
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                        {item.name}
                      </p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-4xl font-bold text-gray-900 dark:text-white">
                          {item.stat}
                        </p>
                        <ArrowTrendingUpIcon className="h-5 w-5 text-green-500 dark:text-green-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </div>
                  </div>

                  {/* Bottom Accent */}
                  <div
                    className={`h-1 bg-gradient-to-r ${item.gradient}`}
                  ></div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-300">
              <SalesGraph />
            </div>
            {user?.role === 'super_admin' && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow duration-300">
                <UsersGraph />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
